import { Box, Chip as MuiChip, Popover, Tooltip, styled } from '@mui/material'
import { useMemo, useRef, useState } from 'react'
import TagColor from './TagColor'
import { FeatureFlag, useFeatureFlag } from 'app/ui/useFeatureFlag'
import EllipsisWithTooltip from 'features/common/ui/utils/EllipsisWithTooltip'
import { Typography } from '@carto/meridian-ds/components'

interface TagsCardFooterProps {
  tags?: string[]
}

const Chip = styled(MuiChip)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
  maxWidth: '100%'
}))

const ChipWrapper = styled(Box)(({ theme }) => ({
  '&:not(:last-child)': {
    marginBottom: theme.spacing(1)
  }
}))

const PopoverMenu = styled(Popover)(({ theme }) => ({
  '.MuiPaper-root': {
    padding: theme.spacing(1)
  }
}))

const MAX_LENGTH_TAG = 12

function TagsCardFooter({ tags }: TagsCardFooterProps) {
  const [openedRef, setOpen] = useState<HTMLElement | null>(null)
  const textElementRef = useRef<HTMLDivElement | null>(null)
  const localRef = useRef<HTMLDivElement | null>(null)
  const hasTagsForResourcesEnabled = useFeatureFlag(FeatureFlag.TagsForResources)

  const hasTags = Boolean(tags?.length)
  const tagToShow = tags?.[0]

  const additionalTags = (tags?.length || 0) - 1

  const open = () => setOpen(localRef.current)
  const close = () => setOpen(null)

  const tagToShowLabel = useMemo(() => {
    if (tagToShow && additionalTags && tagToShow.length > MAX_LENGTH_TAG) {
      return tagToShow.slice(0, MAX_LENGTH_TAG) + '...'
    }
    return tagToShow
  }, [tagToShow, additionalTags])

  if (!hasTags || !hasTagsForResourcesEnabled) return null

  return (
    <Box overflow="hidden">
      <Box display="flex" alignItems="center">
        <EllipsisWithTooltip title={tagToShow} forceTooltip={tagToShowLabel !== tagToShow} textRef={textElementRef}>
          <Chip
            label={
              <Typography ref={textElementRef} component="p" variant="caption" noWrap>
                {tagToShowLabel}
              </Typography>
            }
            size="small"
            variant="outlined"
            color="default"
            avatar={<TagColor name={tagToShow as string} />}
          />
        </EllipsisWithTooltip>
        {Boolean(additionalTags) && (
          <Chip
            ref={localRef}
            onMouseOver={open}
            onMouseLeave={close}
            label={`+${additionalTags}`}
            size="small"
            variant="outlined"
            color="default"
          />
        )}
      </Box>
      <PopoverMenu
        sx={{ pointerEvents: 'none', outline: 'none' }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorEl={openedRef}
        open={Boolean(openedRef)}
        disableAutoFocus={true}
        disableEnforceFocus={true}
        slotProps={{
          paper: {
            onMouseEnter: open,
            onMouseLeave: close,
            sx: { pointerEvents: 'all', maxWidth: (theme) => theme.spacing(20) }
          }
        }}
      >
        {tags?.slice(1).map((tag) => (
          <ChipWrapper key={tag}>
            <Tooltip title={tag}>
              <Chip color="default" size="small" variant="outlined" label={tag} avatar={<TagColor name={tag} />} />
            </Tooltip>
          </ChipWrapper>
        ))}
      </PopoverMenu>
    </Box>
  )
}

export default TagsCardFooter
